import React, {Component, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {Plural, Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {FileSizeFormatter} from '../../../../../srcCommon/formatters/fileSizeFormatter';
import {
  ChannelErrorsEnum,
  getChannelAttachmentsMaxCount,
  getChannelAttachmentsMaxSize,
  getChannelRecipientsCountLimit,
} from '../../../../helpers/models/channelHelpers';
import {SendDraftErrorsEnum} from '../../../../helpers/models/messageHelpers';
import {ChannelModel} from '../../../../models/api/channelModel';
import {ConversationModel} from '../../../../models/api/conversationModel';
import {MessageErrorsEnum} from '../../../../models/api/messageErrorModel';
import {DraftMessageModel} from '../../../../models/api/messageModel';
import {
  ConversationComposerBannerActionsEnum,
  ConversationComposerBannerTypesEnum,
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../services/tracking';
import {ComposerBannerBubble, ComposerBannerThemesEnum} from './composerBannerBubble';

/*
 * Props.
 */

export enum ComposerBannerLevelsEnum {
  INFORMATION = 'INFORMATION',
  WARNING = 'WARNING',
}

export enum MessageTipsEnum {
  CONFLICTING_COPY = 'CONFLICTING_COPY',
  TEAMMATE_FORWARD = 'TEAMMATE_FORWARD',
  MASS_SEND = 'MASS_SEND',
  HAS_DRAFT_ATTACHMENTS = 'HAS_DRAFT_ATTACHMENTS',
  HAS_RAW_VARIABLES = 'HAS_RAW_VARIABLES',
}

export type ComposerBannerErrorsEnum = SendDraftErrorsEnum | MessageErrorsEnum | ChannelErrorsEnum;
type ComposerBannerTypesEnum = ComposerBannerErrorsEnum | MessageTipsEnum;

interface ComposerBannnerOptions {
  type: ComposerBannerTypesEnum;
  level: ComposerBannerLevelsEnum;
  description?: string;
  linkAction?: Handler;
  dismissAction?: Handler;
}

interface ComposerBannerProps {
  channel: ChannelModel;
  conversation: ConversationModel | null;
  message?: DraftMessageModel;
  options?: ComposerBannnerOptions;
  hasResolveDrafts: boolean;
  saveMessage: Handler;
  reloadMessage: Handler;
  onOpenSettings: Handler;
  onResolveMessageConflicts?: Handler;
  isPopup: boolean;
  hasSynchronousTimeline?: boolean;
}

/*
 * Style.
 */

const StyledDescriptionWithIconDiv = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

interface ReloadStyleProps {
  $isWarning: boolean;
}
const StyledReloadDiv = styled.div<ReloadStyleProps>`
  flex-shrink: 0;

  padding: 6px 8px 6px 8px;
  border-radius: 13px;

  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
  text-transform: uppercase;

  &:hover {
    ${(p) => getReloadDivStyle(p.$isWarning)}
  }
`;

const StyledBannerDescriptionDiv = styled.div`
  padding: 10px;
`;

const StyledLinkDiv = styled(StyledReloadDiv)``;

const StyledBannerIconDiv = styled.div`
  display: inline-flex;
  align-items: flex-start;
  margin-right: 7px;
`;

const StyledBannerBoldSpan = styled.span`
  font-weight: ${fontWeights.bold};
  margin-right: 2px;
  margin-left: 2px;
`;

function getReloadDivStyle(isWarning: boolean) {
  if (isWarning) {
    return css`
      color: ${(p) => p.theme.palette.orange.shade50};
      background-color: ${(p) => p.theme.greys.shade20};
    `;
  }

  return css`
    color: ${(p) => p.theme.greys.white};
    background-color: ${(p) => p.theme.palette.blue.shade40};
  `;
}

/*
 * Component.
 */

interface ComposerBannerState {
  lastType?: ComposerBannerTypesEnum;
  isDismissed: boolean;
}

export class ComposerBanner extends Component<ComposerBannerProps, ComposerBannerState> {
  constructor(props: ComposerBannerProps) {
    super(props);
    this.state = {isDismissed: false};
  }

  /*
   * Event handlers.
   */

  private readonly dismiss = () => {
    const {options} = this.props;
    maybeLogBannerClicked(this.props, ConversationComposerBannerActionsEnum.CLOSE);
    // If supplied a dismissAction, call it before toggling dismiss.
    if (options && options.dismissAction) {
      options.dismissAction();
    }

    this.toggleIsDismissed();
  };

  private readonly toggleIsDismissed = () => {
    this.setState(({isDismissed}) => ({isDismissed: !isDismissed}));
  };

  /*
   * Lifecycle.
   */

  componentDidMount() {
    maybeLogBannerViewed(this.props);
  }

  componentDidUpdate(prevProps: ComposerBannerProps, prevState: ComposerBannerState) {
    if (this.state.isDismissed === true) {
      return;
    }

    if (!this.props.options) {
      return;
    }

    if (prevProps.options) {
      return;
    }

    maybeLogBannerViewed(this.props);
  }

  static getDerivedStateFromProps(
    props: Readonly<ComposerBannerProps>,
    state: Readonly<ComposerBannerState>,
  ) {
    const {options} = props;
    if (!options) {
      return null;
    }

    const nextType = options.type;
    const nextHasWarning = options.level === ComposerBannerLevelsEnum.WARNING;

    // Undismiss if banner type changes.
    // Only warnings can be undismissed.
    if (nextHasWarning || (state.lastType && nextType !== state.lastType)) {
      return {lastType: nextType, isDismissed: false};
    }

    return null;
  }

  /*
   * Render.
   */

  render() {
    // We need options to display anything.
    const {options, channel, hasSynchronousTimeline} = this.props;
    if (this.state.isDismissed || !options) {
      return null;
    }

    // Render the description for this error.
    const {type, level} = options;
    const description = options.description || getBannerDescription(type, channel);
    if (!description) {
      return null;
    }

    const isWarning = level === ComposerBannerLevelsEnum.WARNING;
    const theme = isWarning ? ComposerBannerThemesEnum.ORANGE : ComposerBannerThemesEnum.BLUE;

    // Only some errors are dismissable. Such as HAS_DRAFT_ATTACHMENTS.
    const isDismissable =
      options.type === MessageTipsEnum.HAS_DRAFT_ATTACHMENTS ||
      options.level === ComposerBannerLevelsEnum.INFORMATION;
    const onDismiss = isDismissable ? this.dismiss : undefined;

    return (
      <ComposerBannerBubble
        bannerTheme={theme}
        onDismiss={onDismiss}
        shouldShowWarningIcon={isWarning}
        hasSynchronousTimeline={hasSynchronousTimeline}
      >
        <StyledBannerDescriptionDiv>{description}</StyledBannerDescriptionDiv>
        {this.maybeRenderSettingsLink(isWarning)}
        {this.maybeRenderLink(isWarning)}
        {this.maybeRenderRetry(isWarning)}
        {this.maybeRenderReload(isWarning)}
        {this.maybeRenderResolve(isWarning)}
      </ComposerBannerBubble>
    );
  }

  maybeRenderSettingsLink(isWarning: boolean) {
    const {options} = this.props;
    if (
      !options ||
      options.type !== ChannelErrorsEnum.MISSING_O365_SEND_PERMISSIONS ||
      !this.props.onOpenSettings
    ) {
      return null;
    }

    return (
      <ChromelessButton onClick={this.props.onOpenSettings}>
        <StyledLinkDiv $isWarning={isWarning}>
          <Trans id="composer-banner-viewsettings">View settings</Trans>
        </StyledLinkDiv>
      </ChromelessButton>
    );
  }

  maybeRenderLink(isWarning: boolean) {
    const {options} = this.props;
    if (!options || options.type !== MessageTipsEnum.CONFLICTING_COPY || !options.linkAction) {
      return null;
    }

    return (
      <ChromelessButton onClick={options.linkAction}>
        <StyledLinkDiv $isWarning={isWarning}>
          <Trans id="composer-banner-viewmycopy">View my copy</Trans>
        </StyledLinkDiv>
      </ChromelessButton>
    );
  }

  maybeRenderRetry(isWarning: boolean) {
    const {options, saveMessage} = this.props;
    if (!options || options.type !== MessageErrorsEnum.CANT_SAVE) {
      return null;
    }

    return (
      <ChromelessButton onClick={saveMessage}>
        <StyledReloadDiv $isWarning={isWarning}>
          <Trans id="composer-banner-retry">Retry</Trans>
        </StyledReloadDiv>
      </ChromelessButton>
    );
  }

  maybeRenderReload(isWarning: boolean) {
    // Only some errors can be solved by a reload.
    const {options, reloadMessage, onResolveMessageConflicts} = this.props;
    const canResolveConflicts = this.props.hasResolveDrafts && onResolveMessageConflicts;
    if (!options || options.type !== MessageErrorsEnum.VERSION_CONFLICT || canResolveConflicts) {
      return null;
    }

    return (
      <ChromelessButton onClick={reloadMessage}>
        <StyledReloadDiv $isWarning={isWarning}>
          <Trans id="composer-banner-reload">Reload</Trans>
        </StyledReloadDiv>
      </ChromelessButton>
    );
  }

  maybeRenderResolve(isWarning: boolean) {
    // Only version conflict can be solved by a resolve.
    const {options, onResolveMessageConflicts} = this.props;
    const canResolveConflicts = this.props.hasResolveDrafts && onResolveMessageConflicts;
    if (!options || options.type !== MessageErrorsEnum.VERSION_CONFLICT || !canResolveConflicts) {
      return null;
    }

    return (
      <ChromelessButton onClick={onResolveMessageConflicts}>
        <StyledReloadDiv $isWarning={isWarning}>
          <Trans id="composer-banner-resolve">Resolve</Trans>
        </StyledReloadDiv>
      </ChromelessButton>
    );
  }
}

function getBannerDescription(type: ComposerBannerTypesEnum, channel: ChannelModel): ReactNode {
  switch (type) {
    case MessageTipsEnum.TEAMMATE_FORWARD:
      return (
        <Trans id="composer-banner-teammateforward">
          Instead of forwarding this email, try <StyledBannerBoldSpan>@mentioning</StyledBannerBoldSpan> your
          teammate in the comment section below
        </Trans>
      );

    case MessageTipsEnum.MASS_SEND: {
      return (
        <StyledDescriptionWithIconDiv>
          <StyledBannerIconDiv>
            <Icon name="channelCallSmall" />
          </StyledBannerIconDiv>
          <Trans id="composer-banner-masssend">
            <StyledBannerBoldSpan>Mass Text</StyledBannerBoldSpan> - A separate message will be sent to each
            recipient
          </Trans>
        </StyledDescriptionWithIconDiv>
      );
    }

    case SendDraftErrorsEnum.NEEDS_VALIDATION:
      return (
        <Trans id="composer-banner-needsvalidation">You need to validate this channel to send messages</Trans>
      );

    case ChannelErrorsEnum.MISSING_O365_SEND_PERMISSIONS:
      return (
        <Trans id="composer-banner-missingo365sendingpermissions">
          You need to update your O365 permissions.
        </Trans>
      );

    case SendDraftErrorsEnum.TOO_BIG: {
      const maxSize = getChannelAttachmentsMaxSize(channel);
      if (!maxSize) {
        return undefined;
      }

      return (
        <Trans id="composer-banner-toobig">
          You can attach at most <FileSizeFormatter value={maxSize} />
        </Trans>
      );
    }

    case SendDraftErrorsEnum.TOO_MANY_ATTACHMENTS: {
      const maxCount = getChannelAttachmentsMaxCount(channel);
      if (!maxCount) {
        return undefined;
      }

      return (
        <Plural
          id="composer-banner-toomanyattachments"
          value={maxCount}
          one="This channel only supports 1 attachment."
          other="This channel only supports # attachments."
        />
      );
    }

    case SendDraftErrorsEnum.TOO_LONG:
      return <Trans id="composer-banner-toolong">This message is too long</Trans>;

    case SendDraftErrorsEnum.TWITTER_TOO_MANY_GIFS:
      return <Trans id="composer-banner-toomanygifs">Sadly, Twitter only accepts 1 GIF per tweet</Trans>;

    case SendDraftErrorsEnum.TWITTER_MIXED_GIF:
      return (
        <Trans id="composer-banner-differentmediatypes">
          You cannot mix GIFs and regular images in a tweet
        </Trans>
      );

    case SendDraftErrorsEnum.BAD_CONTENT_TYPE:
      return (
        <Trans id="composer-banner-badcontenttype">This channel does not support this file format</Trans>
      );

    case SendDraftErrorsEnum.TOO_MANY_RECIPIENTS:
      return (
        <Trans id="composer-banner-toomanyrecipients">
          This channel is limited to {getChannelRecipientsCountLimit(channel)} recipients per message
        </Trans>
      );

    case SendDraftErrorsEnum.TOO_MANY_RECIPIENTS_TRIAL:
      return (
        <Trans id="composer-banner-toomanyrecipientstrial">
          The limit of recipients is 10 while in trial
        </Trans>
      );

    case MessageErrorsEnum.VERSION_CONFLICT:
      return (
        <Trans id="composer-banner-versionconflict">
          You are not editing the latest version of this draft
        </Trans>
      );

    case MessageErrorsEnum.CANT_SAVE:
      return <Trans id="composer-banner-cantsave">This draft could not be saved</Trans>;

    case MessageTipsEnum.CONFLICTING_COPY:
      return (
        <Trans id="composer-banner-conflictingcopy">We recommend that you reply from your own copy</Trans>
      );

    case MessageTipsEnum.HAS_DRAFT_ATTACHMENTS:
      return (
        <Trans id="composer-banner-draft-attachments">
          Attachments uploading, please try sending again when upload is complete.
        </Trans>
      );

    case MessageTipsEnum.HAS_RAW_VARIABLES:
      return <Trans id="composer-banner-hasrawvariables">Replace or delete all unfilled variables</Trans>;

    default:
      // Some errors like INCOMPLETE should not result in a visible banner.
      return undefined;
  }
}

/*
 * Helpers.
 */

function maybeLogBannerViewed(props: ComposerBannerProps) {
  if (!props.options) {
    return;
  }

  const loggingBannerType = computeLoggingBannerType(props.options.type);
  if (!loggingBannerType) {
    return;
  }

  trackInterfaceEvent(InterfaceEventsEnum.COMPOSER_BANNER_VIEW, {
    namespace: props.channel.namespace,
    draft_id: props.message?.id || null,
    conversation_id: props.conversation?.id || null,
    section: 'p3',
    is_popup: props.isPopup,
    type: loggingBannerType,
    trigger_reason: null,
  });
}

function maybeLogBannerClicked(props: ComposerBannerProps, action: ConversationComposerBannerActionsEnum) {
  if (!props.options) {
    return;
  }

  const loggingBannerType = computeLoggingBannerType(props.options.type);
  if (!loggingBannerType) {
    return;
  }

  trackInterfaceEvent(InterfaceEventsEnum.COMPOSER_BANNER_CLICK, {
    namespace: props.channel.namespace,
    draft_id: props.message?.id || null,
    conversation_id: props.conversation?.id || null,
    section: 'p3',
    is_popup: props.isPopup,
    type: loggingBannerType,
    message_is_reply: Boolean(props.message?.inReplyToId),
    action,
  });
}

function computeLoggingBannerType(bannerType: ComposerBannerTypesEnum) {
  switch (bannerType) {
    case MessageTipsEnum.CONFLICTING_COPY:
      return ConversationComposerBannerTypesEnum.CONFLICTING_COPY;

    case MessageTipsEnum.MASS_SEND:
      return ConversationComposerBannerTypesEnum.MASS_TEXT;

    case MessageTipsEnum.TEAMMATE_FORWARD:
      return ConversationComposerBannerTypesEnum.MENTION_INSTEAD;

    case MessageTipsEnum.HAS_DRAFT_ATTACHMENTS:
      return ConversationComposerBannerTypesEnum.ATTACHMENTS_UPLOADING;

    case MessageTipsEnum.HAS_RAW_VARIABLES:
      return ConversationComposerBannerTypesEnum.RAW_VARIABLES;

    default:
      return undefined;
  }
}
